import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DetailRow, Panel, StatusBadge } from '@/components/kit';
import CopyButton from '@/components/admin/CopyButton';
import { useCostEditor, useOrderCosts } from '@/components/admin/OrderCosts';
import { credits, signedMoney } from '@/shared/ledger';
import { cn } from '@/lib/utils';
import { BOOL_STYLES, ORDER_STATUS_STYLES } from '@/shared/constants';
import { TELLY, money, orderTotal, paymentLabel, planName, termLabel, timeLeft } from '@/shared/pricing';
import { idOf } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const when = (v) => (v ? formatLocalDate(v, DATE_FORMATS.WITH_TIME) : null);

function YesNo({ value }) {
  return <StatusBadge value={value ? 'Yes' : 'No'} styles={BOOL_STYLES} />;
}

function Copyable({ value, label }) {
  if (!value) return null;
  return (
    <span className="flex items-start justify-between gap-2">
      <span className="min-w-0 break-all font-mono text-sm">{value}</span>
      <CopyButton text={value} label={label} size="sm" variant="ghost" />
    </span>
  );
}

/** Credits used, cost, collected and profit for one order, from the ledger. Editable in place. */
function ProfitRows({ order, refreshKey, onCostsSaved }) {
  const costs = useOrderCosts([order.Id]);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    costs.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const [edit, dialog] = useCostEditor(() => (onCostsSaved ? onCostsSaved() : costs.run()));

  const s = costs.data?.[order.Id];
  const loading = costs.loading && !costs.data;
  const typed = !!s?.typed;
  const logged = Number(s?.collected) || 0;
  const fallback = !logged;
  const collected = fallback ? Number(orderTotal(order)) || 0 : logged;
  const cost = Number(s?.cost) || 0;
  const profit = collected - cost;

  const value = (node) => (loading ? <Skeleton className="h-5 w-20" /> : node);
  const muted = (text) => <span className="text-muted-foreground">{text}</span>;

  return (
    <>
      <DetailRow label="Credits used">
        {value(typed ? <span className="font-semibold tabular-nums">{credits(s.credits)} cr</span> : muted('None logged yet'))}
      </DetailRow>
      <DetailRow label="Cost to you">
        {value(typed ? <span className="tabular-nums">{signedMoney(cost)}</span> : muted('Not entered'))}
      </DetailRow>
      <DetailRow label="Collected">
        {value(
          <span className="tabular-nums">
            {signedMoney(collected)}
            {fallback && <span className="block text-xs text-muted-foreground">Order total. Nothing logged as collected.</span>}
          </span>,
        )}
      </DetailRow>
      <DetailRow label="Profit / loss">
        {value(
          typed ? (
            <span className={cn('font-semibold tabular-nums', profit >= 0 ? 'text-success' : 'text-destructive')}>
              {signedMoney(profit)}
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">{profit >= 0 ? 'profit' : 'loss'}</span>
            </span>
          ) : (
            muted('Add your cost to see it')
          ),
        )}
      </DetailRow>
      <div className="flex flex-wrap items-center justify-between gap-2 py-3">
        <p className="text-xs text-muted-foreground">
          {costs.error
            ? 'Could not load the ledger.'
            : s?.count > 1
              ? `Totals from ${s.count} ledger entries. Only the team sees this.`
              : 'From the ledger. Only the team sees this.'}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => edit(order, s)} disabled={loading}>
          <ApperIcon name="Pencil" size={16} />
          {typed ? 'Edit credits and cost' : 'Add credits and cost'}
        </Button>
      </div>
      {dialog}
    </>
  );
}

/** Panels showing every field on an order. Billing carries the editable credits and cost. */
export default function OrderFacts({ order, refreshKey, onCostsSaved }) {
  const customerId = idOf(order.customer_c);
  const left = timeLeft(order.ends_at_c);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Customer" icon="User">
        <dl className="divide-y divide-border">
          <DetailRow label="Customer record">
            {customerId ? (
              <Link to={`/admin/customers/${customerId}`} className="text-primary hover:underline">
                {order.customer_c?.Name ?? 'View customer'}
              </Link>
            ) : null}
          </DetailRow>
          <DetailRow label="Full name">{order.full_name_c}</DetailRow>
          <DetailRow label="Email">
            {order.email_c ? (
              <a href={`mailto:${order.email_c}`} className="text-primary hover:underline">
                {order.email_c}
              </a>
            ) : null}
          </DetailRow>
          <DetailRow label="Contact method">{order.contact_method_c}</DetailRow>
          <DetailRow label="Handle">{order.contact_handle_c}</DetailRow>
          <DetailRow label="Verified Email">
            <YesNo value={order.email_verified_c} />
          </DetailRow>
          <DetailRow label="Device">{order.device_c}</DetailRow>
        </dl>
      </Panel>

      <Panel title="Package" icon="Tv">
        <dl className="divide-y divide-border">
          <DetailRow label="Plan">{planName(order.plan_c)}</DetailRow>
          <DetailRow label="Term">{termLabel(order.plan_c, order.term_c)}</DetailRow>
          <DetailRow label={order.plan_c === 'Telly' ? 'Licences' : 'Connections'}>{order.connections_c}</DetailRow>
          <DetailRow label="Adult content">
            <YesNo value={order.adult_content_c} />
          </DetailRow>
          <DetailRow label="Wants a trial">
            <YesNo value={order.wants_trial_c} />
          </DetailRow>
          <DetailRow label="TV guide (EPG)">
            <YesNo value={order.include_epg_c} />
          </DetailRow>
          <DetailRow label={`${TELLY.name} add-on`}>
            <YesNo value={order.telly_addon_c} />
          </DetailRow>
          <DetailRow label="Add-on licences">{order.telly_addon_c ? order.telly_qty_c : null}</DetailRow>
        </dl>
      </Panel>

      <Panel title="Billing" icon="Wallet">
        <dl className="divide-y divide-border">
          <DetailRow label="Price">{money(order.price_c)}</DetailRow>
          <DetailRow label="Discount code">
            {order.discount_code_c ? <span className="font-mono">{order.discount_code_c}</span> : null}
          </DetailRow>
          <DetailRow label="Discount">{Number(order.discount_c) ? `−${money(order.discount_c)}` : null}</DetailRow>
          <DetailRow label="Referred by">
            {order.referred_by_c ? (
              <span>
                <span className="font-mono">{order.referred_by_c}</span>
                <span className="block text-xs text-muted-foreground">
                  {order.referral_counted_c ? 'Referral counted' : 'Counts on activation if the term is 6 months or longer'}
                </span>
              </span>
            ) : null}
          </DetailRow>
          <DetailRow label="Total">
            <span className="font-semibold">{money(orderTotal(order))}</span>
          </DetailRow>
          <DetailRow label="Currency">{order.currency_c}</DetailRow>
          <DetailRow label="Payment method">{paymentLabel(order.payment_method_c)}</DetailRow>
          <DetailRow label="No-refund accepted">{when(order.refund_accepted_at_c)}</DetailRow>
          <ProfitRows order={order} refreshKey={refreshKey} onCostsSaved={onCostsSaved} />
        </dl>
      </Panel>

      <Panel title="Line details" icon="KeyRound">
        <dl className="divide-y divide-border">
          <DetailRow label="Server URL">
            <Copyable value={order.line_server_c} label="Server URL" />
          </DetailRow>
          <DetailRow label="Username">
            <Copyable value={order.line_username_c} label="Username" />
          </DetailRow>
          <DetailRow label="Password">
            <Copyable value={order.line_password_c} label="Password" />
          </DetailRow>
          <DetailRow label="Note">
            {order.line_note_c ? <span className="whitespace-pre-wrap">{order.line_note_c}</span> : null}
          </DetailRow>
          <DetailRow label="Credentials sent">{when(order.credentials_sent_at_c)}</DetailRow>
          <DetailRow label="Free months owed">{Number(order.gift_months_c) ? order.gift_months_c : null}</DetailRow>
        </dl>
      </Panel>

      <Panel title={`${TELLY.name} key`} icon="MonitorPlay">
        <dl className="divide-y divide-border">
          <DetailRow label="Licence key">
            {order.telly_key_c ? (
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0 whitespace-pre-wrap break-all font-mono text-sm">{order.telly_key_c}</span>
                <CopyButton text={order.telly_key_c} label="Licence key" size="sm" variant="ghost" />
              </span>
            ) : null}
          </DetailRow>
          <DetailRow label="Key sent">{when(order.telly_sent_at_c)}</DetailRow>
        </dl>
      </Panel>

      <Panel title="Dates and reminders" icon="CalendarClock">
        <dl className="divide-y divide-border">
          <DetailRow label="Status">
            <StatusBadge value={order.status_c} styles={ORDER_STATUS_STYLES} />
          </DetailRow>
          <DetailRow label="Placed">{when(order.CreatedOn)}</DetailRow>
          <DetailRow label="Activated">{when(order.activated_at_c)}</DetailRow>
          <DetailRow label="Term ends">
            {order.ends_at_c ? (
              <span>
                {when(order.ends_at_c)}
                {left && <span className="block text-xs text-muted-foreground">{left}</span>}
              </span>
            ) : null}
          </DetailRow>
          <DetailRow label="Renewal requested">{when(order.renew_requested_at_c)}</DetailRow>
          <DetailRow label="Expiry reminder sent">{when(order.expiry_emailed_at_c)}</DetailRow>
          <DetailRow label="Win-back sent">{when(order.winback_emailed_at_c)}</DetailRow>
        </dl>
      </Panel>

      <Panel title="Internal notes" icon="NotebookPen" className="lg:col-span-2" bodyClassName="py-3">
        {order.notes_c ? (
          <p className="whitespace-pre-wrap text-sm">{order.notes_c}</p>
        ) : (
          <p className="text-sm text-muted-foreground">No notes. Only the team sees these.</p>
        )}
      </Panel>
    </div>
  );
}
